import React, { useContext, useState } from 'react';
import { Redirect } from 'react-router-dom';

import { Visible } from '../../core/components';
import { UserContext } from '../../contexts';
import { LoadingOverlay, Money } from '../../components'
import { Card, Segmented } from '../../components/ui';
import {
  BankTransfer,
  CardPayment,
  CheckoutItem,
  DeliveryAddress,
} from '../molecules';
import { useCheckout } from '../checkoutState';
import { getBaseCurrency } from "../../core/currency";
import '../checkout.scss';

const ONLINE_PAYMENT_LIMIT = 3000 * 100; // 3000 GBP

// Display copy only — the value handed to setSelectedPaymentMethod is still the
// raw method id the checkout state and the backend use.
const PAYMENT_METHOD_LABELS = { transfer: 'Bank transfer', card: 'Card' };

export function Checkout({ readOnly = false }) {
  const { user } = useContext(UserContext)
  const checkout = useCheckout();

  const [isChangingAddress, setIsChangingAddress] = useState(false);

  const onDeliveryAddressSubmit = (address) => {
    checkout.setShippingAddress(address);
    setIsChangingAddress(false);
  };

  if (
    !isChangingAddress && (
      !checkout.shippingAddress?.line1 ||
      !checkout.shippingAddress?.postal_code ||
      !checkout.shippingAddress?.country_id ||
      !checkout.shippingAddress?.locality
    )
  ) {
    setIsChangingAddress(true);
  }

  const canPayOnline = checkout.total < ONLINE_PAYMENT_LIMIT;

  if (!checkout.isLoading && !checkout?.count) {
    return <Redirect to="/" />
  }

  return (
    <main id='checkout' className="checkout">
      <section className="checkout__section">
        <h3 className="checkout__heading">Your Order</h3>

        <div className="checkout__items">
          {checkout.items?.map((item) => (
            <CheckoutItem
              key={item.id}
              {...{
                item,
                readOnly: readOnly || checkout.isOrderPaid,
                removeItem: () => checkout.removeItem(item.id),
                toggleDeposit: checkout.toggleDeposit,
                isOrderPaid: !checkout.payableAmount
              }}
            />
          ))}
        </div>

        <Visible when={!checkout.areDepositsSelected}>
          <Card className="checkout-totals">
            <div className="checkout-totals__row">
              <h3 className="checkout-totals__label">Total:</h3>
              {/* Checkout totals aggregate listing prices — deliberate store-base renders. */}
              <Money className="checkout-totals__amount" amount={checkout.total} currency={getBaseCurrency()} />
            </div>
            <Visible when={checkout.paidAmount && checkout.total > checkout.paidAmount}>
              <div className="checkout-totals__row">
                <h3 className="checkout-totals__label">Total Remaining:</h3>
                <Money className="checkout-totals__amount" amount={checkout.total - checkout.paidAmount} currency={getBaseCurrency()} />
              </div>
            </Visible>
          </Card>
        </Visible>
        <Visible when={checkout.areDepositsSelected}>
          <Card className="checkout-totals">
            <div className="checkout-totals__row">
              <h3 className="checkout-totals__label">Deposit Total:</h3>
              <Money className="checkout-totals__amount" amount={checkout.selectedDepositsAmount} currency={getBaseCurrency()} />
            </div>
          </Card>
        </Visible>
      </section>

      <section className="checkout__section">
        <h3 className="checkout__heading">Delivery Address</h3>

        <DeliveryAddress
          mode={isChangingAddress && !readOnly && !checkout.isOrderPaid ? 'edit' : 'display'}
          readOnly={readOnly || checkout.isOrderPaid}
          address={checkout.shippingAddress}
          onEdit={() => setIsChangingAddress(true)}
          onCancel={() => setIsChangingAddress(false)}
          onSubmit={onDeliveryAddressSubmit}
        />
      </section>

      <Visible when={checkout.payableAmount}>
        <section className="checkout__section checkout-payment">
          <h3 className="checkout__heading">Payment</h3>

          <Segmented
            fullWidth
            label="Payment method"
            className="checkout-payment__methods"
            value={checkout.selectedPaymentMethod}
            onChange={checkout.setSelectedPaymentMethod}
            options={(checkout.availablePaymentMethods ?? []).map((method) => ({
              value: method,
              label: PAYMENT_METHOD_LABELS[method] ?? method,
            }))}
          />

          <Visible when={checkout.selectedPaymentMethod === 'transfer'}>
            <BankTransfer
              transferRefNo={checkout.transferRefNo}
              amount={checkout.payableAmount}
              confirmPayment={checkout.pay}
              canPayOnline={canPayOnline && checkout.availablePaymentMethods?.includes('card')}
              payOnline={() => checkout.setSelectedPaymentMethod('card')}
            />
          </Visible>

          <Visible when={checkout.selectedPaymentMethod === 'card'}>
            <CardPayment
              user={user}
              amount={checkout.payableAmount}
              shippingAddress={checkout.shippingAddress}
              createPayment={checkout.pay}
              onPaymentConfirmed={checkout.onPaymentConfirmed}
            />
          </Visible>
        </section>
      </Visible>

      <Visible when={checkout.isLoading || checkout.submitting}>
        <LoadingOverlay type="submitting" text={checkout.isLoading ? 'Loading...' : 'Processing...'} />
      </Visible>
    </main>
  );
};
